import { useState, useRef, useEffect, useCallback } from "react";
import DiagnosisStep from "@/pages/diagnosis/components/DiagnosisStep";
import DiagnosisResult from "@/pages/diagnosis/components/DiagnosisResult";
import { useParallax } from "@/hooks/useParallax";


const Q1_OPTIONS = [
  { id: "q1_access", label: "アクセスや問い合わせが増えない", icon: "ri-signal-wifi-off-line" },
  { id: "q1_cost", label: "販促費にお金を沢山掛けられない", icon: "ri-money-dollar-circle-line" },
  { id: "q1_design", label: "サイトやデザインが古く、ブランドイメージを損ねている", icon: "ri-layout-line" },
  { id: "q1_unknown", label: "何から手を付ければ良いか全く分からない", icon: "ri-question-line" },
];

const Q2_OPTIONS = [
  { id: "q2_sales", label: "とにかく売上・お問い合わせを増やしたい", icon: "ri-line-chart-line" },
  { id: "q2_awareness", label: "新しい層への認知を爆発的に広げたい", icon: "ri-megaphone-line" },
  { id: "q2_repeat", label: "リピーターへの定着率を上げたい", icon: "ri-repeat-line" },
  { id: "q2_brand", label: "長期的なブランディングと信頼を確立したい", icon: "ri-shield-star-line" },
];

type Phase = "q1" | "q2" | "analyzing" | "result";

function RippleButton({ children, onClick, className, style }: {
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
  style?: React.CSSProperties;
}) {
  const btnRef = useRef<HTMLButtonElement>(null);

  const createRipple = (e: React.MouseEvent<HTMLButtonElement>) => {
    const btn = btnRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const ripple = document.createElement("span");
    const size = Math.max(rect.width, rect.height);
    ripple.style.width = ripple.style.height = `${size}px`;
    ripple.style.left = `${e.clientX - rect.left - size / 2}px`;
    ripple.style.top = `${e.clientY - rect.top - size / 2}px`;
    ripple.className = "ripple-effect";
    btn.appendChild(ripple);
    setTimeout(() => ripple.remove(), 600);
    onClick?.();
  };

  return (
    <button
      ref={btnRef}
      onClick={createRipple}
      className={className}
      style={{ position: "relative", overflow: "hidden", ...style }}
    >
      {children}
    </button>
  );
}

interface DiagnosisSectionProps {
  theme?: "light" | "dark";
}

export default function DiagnosisSection({ theme = "light" }: DiagnosisSectionProps) {
  const isDark = theme === "dark";
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);
  const [phase, setPhase] = useState<Phase>("q1");
  const [q1, setQ1] = useState<string | null>(null);
  const [q2, setQ2] = useState<string | null>(null);
  const [stepVisible, setStepVisible] = useState(true);
  const [analyzeProgress, setAnalyzeProgress] = useState(0);
  const parallaxRef = useParallax(0.04);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.05, rootMargin: "0px 0px -50px 0px" }
    );
    if (sectionRef.current) obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (phase !== "analyzing") return;
    setAnalyzeProgress(0);
    const start = Date.now();
    const duration = 1800;
    let rafId: number;
    const tick = () => {
      const elapsed = Date.now() - start;
      const p = Math.min(elapsed / duration, 1);
      const eased = p < 0.5 ? 2 * p * p : -1 + (4 - 2 * p) * p;
      setAnalyzeProgress(Math.round(eased * 100));
      if (p < 1) rafId = requestAnimationFrame(tick);
      else {
        setTimeout(() => {
          setStepVisible(false);
          setTimeout(() => { setPhase("result"); setStepVisible(true); }, 350);
        }, 200);
      }
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [phase]);

  const handleQ1Next = () => {
    if (!q1) return;
    setStepVisible(false);
    setTimeout(() => { setPhase("q2"); setStepVisible(true); }, 350);
  };

  const handleQ2Next = () => {
    if (!q2) return;
    setStepVisible(false);
    setTimeout(() => { setPhase("analyzing"); setStepVisible(true); }, 350);
  };

  const handleRetry = () => {
    setStepVisible(false);
    setTimeout(() => {
      setQ1(null); setQ2(null); setPhase("q1"); setStepVisible(true);
    }, 350);
  };

  const circumference = 2 * Math.PI * 52;

  return (
    <section
      id="diagnosis"
      ref={sectionRef}
      className="relative overflow-hidden"
      style={{
        background: isDark ? "#111111" : "linear-gradient(to right, #e8ddd8, #ffffff)",
        borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.12)"}`,
        scrollMarginTop: "80px",
      }}
    >
      {/* Diagonal decorative lines */}

      <div ref={parallaxRef} className="relative z-10 px-4 md:px-8 lg:px-20 pt-16 md:pt-28 pb-16 md:pb-28 parallax-slow">
        {/* Inner bordered container */}
        <div style={{
          border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "#111111"}`,
          background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
          padding: "clamp(20px, 4vw, 64px)",
          maxWidth: "760px",
          margin: "0 auto",
          position: "relative",
        }}>
          {/* Corner accents */}
          {(["top-0 left-0", "top-0 right-0", "bottom-0 left-0", "bottom-0 right-0"] as const).map((pos, i) => (
            <div key={i} className={`absolute ${pos} w-3 h-3 pointer-events-none`} style={{
              borderTop: i < 2 ? "2px solid #c00000" : "none",
              borderBottom: i >= 2 ? "2px solid #c00000" : "none",
              borderLeft: i % 2 === 0 ? "2px solid #c00000" : "none",
              borderRight: i % 2 === 1 ? "2px solid #c00000" : "none",
            }} />
          ))}

          {/* Section header with AnimatedTitle */}
          <div
            className="mb-6 md:mb-10"
            style={{
              opacity: visible ? 1 : 0,
              transform: visible ? "translateY(0)" : "translateY(24px)",
              transition: "opacity 0.7s ease, transform 0.7s cubic-bezier(0.22,1,0.36,1)",
            }}
          >
            {/* Top label */}
            <div className="mb-5 text-center">
              <p 
                style={{ 
                  fontFamily: "'Barlow', sans-serif", 
                  fontSize: "clamp(10px, 1vw, 12px)", 
                  letterSpacing: "0.45em", 
                  color: "#c00000",
                  opacity: visible ? 1 : 0,
                  transform: visible ? "translateY(0)" : "translateY(8px)",
                  transition: "opacity 0.5s ease, transform 0.5s cubic-bezier(0.22,1,0.36,1) 0.1s",
                }}
              >
                FREE DIAGNOSIS
              </p>
            </div>

            {/* Big title — char-by-char animation via AnimatedTitle */}
            <div className="flex flex-col items-center gap-3">
              <h2
                style={{
                  fontFamily: "'Noto Sans JP', sans-serif",
                  fontSize: "clamp(28px, 6vw, 56px)",
                  fontWeight: 700,
                  lineHeight: 1.0,
                  color: isDark ? "#ffffff" : "#111111",
                  letterSpacing: "-0.02em",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textAlign: "center",
                }}
              >
                {["無", "料", "診", "断"].map((char, ci) => (
                  <span
                    key={ci}
                    style={{
                      display: "inline-block",
                      color: ci < 2 ? "#c00000" : (isDark ? "#ffffff" : "#111111"),
                      opacity: visible ? 1 : 0,
                      transform: visible
                        ? "translateX(0)"
                        : "translateX(-40px)",
                      transition: `opacity 0.5s ease ${0.05 + ci * 0.08}s, transform 0.6s cubic-bezier(0.22,1,0.36,1) ${0.05 + ci * 0.08}s`,
                    }}
                  >
                    {char}
                  </span>
                ))}
              </h2>
            </div>

            {/* Divider with enhanced animation */}
            <div 
              style={{
                height: "1px",
                background: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.12)",
                marginTop: "20px",
                transform: visible ? "scaleX(1)" : "scaleX(0)",
                transformOrigin: "center",
                transition: "transform 0.9s cubic-bezier(0.22,1,0.36,1) 0.3s",
              }} 
            />
          </div>

          {/* Diagnosis content */}
          <div
            style={{
              opacity: visible ? 1 : 0,
              transition: "opacity 0.7s ease 0.2s",
            }}
          >
            {phase === "q1" && (
              <DiagnosisStep
                stepNum={1} totalSteps={2}
                question="現在、1番の「お悩み」はどれですか？"
                subText="あなたのビジネスが今最も直面している課題を選んでください。"
                options={Q1_OPTIONS}
                selected={q1} onSelect={setQ1} onNext={handleQ1Next}
                visible={stepVisible}
                isDark={isDark}
              />
            )}
            {phase === "q2" && (
              <DiagnosisStep
                stepNum={2} totalSteps={2}
                question="Webを活用して、一番「達成したいこと」は何ですか？"
                subText="あなたのビジネスがWebで実現したい目標を選んでください。"
                options={Q2_OPTIONS}
                selected={q2} onSelect={setQ2} onNext={handleQ2Next}
                visible={stepVisible}
                isDark={isDark}
              />
            )}
            {phase === "analyzing" && (
              <div
                className="flex flex-col items-center justify-center py-16"
                style={{
                  opacity: stepVisible ? 1 : 0,
                  transition: "opacity 0.4s ease",
                }}
              >
                <div className="relative mb-8" style={{ width: 120, height: 120 }}>
                  <svg width="120" height="120" style={{ transform: "rotate(-90deg)" }}>
                    <circle cx="60" cy="60" r="52" fill="none" stroke={isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.06)"} strokeWidth="4" />
                    <circle
                      cx="60" cy="60" r="52" fill="none"
                      stroke="#c00000" strokeWidth="4"
                      strokeLinecap="round"
                      strokeDasharray={circumference}
                      strokeDashoffset={circumference * (1 - analyzeProgress / 100)}
                      style={{ transition: "stroke-dashoffset 0.05s linear" }}
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="counter-num" style={{ fontFamily: "'DM Mono', monospace", fontSize: "22px", fontWeight: 700, color: isDark ? "#ffffff" : "#111111" }}>
                      {analyzeProgress}
                    </span>
                  </div>
                </div>
                <p style={{ fontFamily: "'Barlow', sans-serif", fontSize: "12px", letterSpacing: "0.4em", color: isDark ? "rgba(255,255,255,0.3)" : "#111111" }}>
                  ANALYZING...
                </p>
                <p style={{ fontFamily: "'Noto Sans JP', sans-serif", fontSize: "15px", color: isDark ? "rgba(255,255,255,0.35)" : "#111111", marginTop: "8px" }}>
                  診断中です。少々お待ちください。
                </p>
              </div>
            )}
            {phase === "result" && q1 && q2 && (
              <div style={{ animation: "scaleIn 0.6s cubic-bezier(0.22,1,0.36,1) both" }}>
                <DiagnosisResult q1={q1} q2={q2} onRetry={handleRetry} isDark={isDark} />
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}